import {Landmark, DollarSign, Heart, GitBranch, ArrowRight} from 'lucide-react'
import { Link } from 'react-router-dom'

interface ToolCategory {
  icon: React.ReactNode
  title: string
  color: string
  bgColor: string
  tools: { name: string; description: string; keywords: string; href?: string; live?: boolean }[]
}

const TOOL_CATEGORIES: ToolCategory[] = [
  {
    icon: <Landmark className="w-6 h-6" />,
    title: "Land Management",
    color: "#2D6A4F",
    bgColor: "#B7E4C7",
    tools: [
      {
        name: "Cattle Per Acre Calculator",
        description: "Determine the optimal stocking rate for your pasture based on land quality, forage type, and cattle weight. Avoid overgrazing and maximize productivity.",
        keywords: "stocking rate, pasture management, cattle per acre",
        href: "/cattle-per-acre-calculator",
        live: true,
      },
    ],
  },
  {
    icon: <DollarSign className="w-6 h-6" />,
    title: "Business & Profit",
    color: "#D4A373",
    bgColor: "#FEFAE0",
    tools: [
      {
        name: "Cow Calf Profit Calculator",
        description: "Estimate your cow-calf operation profitability by factoring in feed costs, veterinary expenses, market prices, and revenue per head.",
        keywords: "cow calf profit, ranch profitability, cattle business",
        href: "/cow-calf-profit-calculator",
        live: true,
      },
      {
        name: "Cow Calf Cost of Production",
        description: "Break down your total cost of production per calf including labor, feed, health, breeding, and overhead expenses.",
        keywords: "cost of production, cow calf expenses, ranch economics",
        href: "/cow-calf-cost-of-production",
        live: true,
      },
    ],
  },
  {
    icon: <Heart className="w-6 h-6" />,
    title: "Health & Feeding",
    color: "#E76F51",
    bgColor: "#fce8e2",
    tools: [
      {
        name: "Cow Weight Calculator",
        description: "Estimate your cattle's weight using heart girth and body length measurements. Essential for dosing medication and tracking growth.",
        keywords: "cattle weight estimation, cow weight, livestock measurement",
      },
      {
        name: "Dairy Cow Ration Calculator",
        description: "Formulate balanced dairy rations based on milk production level, body weight, and stage of lactation. Optimize feed efficiency and milk yield.",
        keywords: "dairy cow ration, feed calculator, dairy nutrition",
      },
    ],
  },
  {
    icon: <GitBranch className="w-6 h-6" />,
    title: "Breeding Tools",
    color: "#606C38",
    bgColor: "#e8ecd6",
    tools: [
      {
        name: "Cattle Heat Cycle Calculator",
        description: "Track your cow's estrous cycle and predict the next standing heat. Improve conception rates with precise breeding timing.",
        keywords: "heat cycle, estrous cycle, cattle breeding timing",
      },
      {
        name: "Cattle Herd Growth Calculator",
        description: "Project your herd size over time based on calving rate, retention rate, and culling decisions. Plan for expansion or maintenance.",
        keywords: "herd growth, cattle expansion, herd projection",
      },
    ],
  },
]

export default function ToolsSection() {
  return (
    <section id="tools" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block text-[#40916C] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Livestock Utility Suite
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#283618]">
            Niche Agricultural Calculators
          </h2>
          <p className="mt-3 text-[#606C38] text-base sm:text-lg max-w-2xl mx-auto">
            From pasture management to dairy nutrition, our specialized tools help you make data-driven decisions for your cattle operation.
          </p>
        </div>

        {/* Tool Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {TOOL_CATEGORIES.map((category) => (
            <div
              key={category.title}
              className="group bg-[#FEFAE0] rounded-2xl border border-[#B7E4C7]/40 overflow-hidden hover:shadow-lg hover:shadow-[#2D6A4F]/5 transition-all duration-300 hover:-translate-y-1"
            >
              {/* Category Header */}
              <div className="px-6 py-4 flex items-center gap-3 border-b border-[#B7E4C7]/30">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: category.bgColor, color: category.color }}
                >
                  {category.icon}
                </div>
                <h3 className="font-bold text-lg text-[#283618]" style={{ fontFamily: "'Merriweather', serif" }}>
                  {category.title}
                </h3>
              </div>

              {/* Tools List */}
              <div className="p-6 space-y-4">
                {category.tools.map((tool) => {
                  const Wrapper = tool.href ? Link : 'div'
                  const wrapperProps = tool.href ? { to: tool.href } : {}
                  return (
                    <Wrapper
                      key={tool.name}
                      {...(wrapperProps as any)}
                      className="group/tool cursor-pointer block"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: category.color }} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-[#283618] group-hover/tool:text-[#2D6A4F] transition-colors">
                              {tool.name}
                            </h4>
                            {tool.live ? (
                              <span className="inline-flex items-center gap-1 bg-[#2D6A4F]/10 text-[#2D6A4F] text-[10px] font-bold px-2 py-0.5 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#2D6A4F] animate-pulse" />
                                LIVE
                              </span>
                            ) : null}
                            <ArrowRight className="w-3.5 h-3.5 text-[#B7E4C7] group-hover/tool:text-[#2D6A4F] group-hover/tool:translate-x-1 transition-all" />
                          </div>
                          <p className="mt-1 text-sm text-[#606C38] leading-relaxed">
                            {tool.description}
                          </p>
                        </div>
                      </div>
                    </Wrapper>
                  )
                })}
              </div>

              {/* Status Badge */}
              <div className="px-6 pb-4">
                {category.tools.some((t) => t.live) ? (
                  <div className="inline-flex items-center gap-3">
                    {category.tools.some((t) => t.live) ? (
                      <div className="inline-flex items-center gap-1.5 bg-[#2D6A4F]/10 text-[#2D6A4F] text-xs font-semibold px-3 py-1.5 rounded-full">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#2D6A4F] animate-pulse" />
                        Available Now
                      </div>
                    ) : null}
                    {category.tools.some((t) => !t.live) ? (
                      <div className="inline-flex items-center gap-1.5 bg-[#D4A373]/15 text-[#D4A373] text-xs font-semibold px-3 py-1.5 rounded-full">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#D4A373]" />
                        More Coming Soon
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 bg-[#D4A373]/15 text-[#D4A373] text-xs font-semibold px-3 py-1.5 rounded-full">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4A373]" />
                    Coming Soon
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
